import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Spinner,
  Alert,
  Modal,
  Form,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaUsers,
  FaBookOpen,
  FaInfoCircle,
  FaCheck,
  FaTimes,
  FaTrash,
  FaEdit,
} from "react-icons/fa";
import { useProjectContext } from "../../contextProviders/projectContext";
import Sidebar from "../../components/SideBar";
import TopNavBar from "../../components/topNavBar";
import axios from "axios";
import { API_BASE_URL } from "../../api";
import { deduplicateReflections } from "../../utils/reflections";

const AdminProjectDetailView = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { projects, loading: projectsLoading, fetchProjects, deleteProject } = useProjectContext();

  const [activeTab, setActiveTab] = useState(state?.activeTab || "overview");
  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);

  // Delete Project State
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletingReflectionId, setDeletingReflectionId] = useState(null);

  // Edit Log State
  const [showEditLogModal, setShowEditLogModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [editedStartTime, setEditedStartTime] = useState("");
  const [editedEndTime, setEditedEndTime] = useState("");
  const [editLogError, setEditLogError] = useState(null);
  const [editLogSuccess, setEditLogSuccess] = useState(false);

  const handleDeleteProjectSubmit = async (e) => {
    e.preventDefault();
    if (deleteConfirmInput !== "DELETE") return;
    
    setDeleteLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      await deleteProject(projectId);
      setActionSuccess(`Project "${project.name}" successfully deleted!`);
      setTimeout(() => {
        setShowDeleteConfirm(false);
        setDeleteConfirmInput("");
        navigate("/admin/projects");
      }, 1500);
    } catch (err) {
      setActionError(err.message || "Failed to delete project. Please try again.");
      setShowDeleteConfirm(false);
      setDeleteConfirmInput("");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Lightbox Modal State
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const project = projects.find((p) => p._id === projectId);
  const pendingAmendment = project?.amendments?.find(
    (amendment) => amendment.status === "pending"
  );
  const uniqueReflections = deduplicateReflections(project?.reflections);

  const handleDeleteReflection = async (reflectionId) => {
    if (!reflectionId || deletingReflectionId) return;
    const confirmed = window.confirm(
      "Delete this volunteer reflection? This action cannot be undone."
    );
    if (!confirmed) return;

    setDeletingReflectionId(reflectionId);
    setActionError(null);
    setActionSuccess(null);
    try {
      await axios.delete(
        `${API_BASE_URL}/projects/${projectId}/reflections/${reflectionId}`,
        { headers: getAuthHeader() }
      );
      await fetchProjects();
      setActionSuccess("Reflection deleted successfully.");
    } catch (error) {
      setActionError(
        error.response?.data?.error || "Failed to delete reflection."
      );
    } finally {
      setDeletingReflectionId(null);
    }
  };

  const reviewAmendment = async (status) => {
    if (!pendingAmendment) return;
    setActionError(null);
    try {
      await axios.patch(
        `${API_BASE_URL}/projects/${projectId}/amendments/${pendingAmendment._id}`,
        { status },
        { headers: getAuthHeader() }
      );
      await fetchProjects();
      setActionSuccess(`Project amendment ${status}.`);
    } catch (error) {
      setActionError(error.response?.data?.error || "Unable to review the amendment.");
    }
  };

  // If projects context is empty on page refresh, fetch it
  useEffect(() => {
    if (projects.length === 0 && !projectsLoading) {
      fetchProjects();
    }
  }, [projects, projectsLoading, fetchProjects]);

  // Fetch participants and logs for this project
  const loadData = async () => {
    if (!projectId) return;
    try {
      setParticipantsLoading(true);
      setLogsLoading(true);

      const [pRes, lRes] = await Promise.all([
        axios.get(
          `${API_BASE_URL}/projects/${projectId}/participants`,
          { headers: getAuthHeader() }
        ),
        axios.get(
          `${API_BASE_URL}/projects/${projectId}/logs`,
          { headers: getAuthHeader() }
        ),
        fetchProjects(), // Sync projects context with DB to refresh reflections/creator info
      ]);

      setParticipants(pRes.data || []);
      setLogs(lRes.data || []);
    } catch (err) {
      console.error("Error loading project details data:", err);
      setActionError("Failed to fetch participant or log sheets.");
    } finally {
      setParticipantsLoading(false);
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [projectId]);

  const handleApproveDenyLog = async (logId, status) => {
    try {
      setActionError(null);
      setActionSuccess(null);

      const response = await axios.post(
        `${API_BASE_URL}/participations/approve-hours`,
        {
          projectId,
          logId,
          status,
        },
        { headers: getAuthHeader() }
      );

      setActionSuccess(`Hours successfully ${status}!`);
      // Reload participant and logs data
      loadData();
      
      // Clear success notification after 3 seconds
      setTimeout(() => {
        setActionSuccess(null);
      }, 3000);
    } catch (err) {
      console.error("Error updating log status:", err);
      setActionError(err.response?.data?.error || "Failed to update log entry status.");
    }
  };

  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  const handleEditLogClick = (log) => {
    setEditingLog(log);
    setEditedStartTime(formatDateTimeForInput(log.startTime));
    setEditedEndTime(formatDateTimeForInput(log.endTime));
    setEditLogError(null);
    setEditLogSuccess(false);
    setShowEditLogModal(true);
  };

  const handleEditLogSubmit = async (e) => {
    if (e) e.preventDefault();
    setEditLogError(null);
    setEditLogSuccess(false);

    if (!editedStartTime || !editedEndTime) {
      setEditLogError("Please select both start and end times.");
      return;
    }

    try {
      const start = new Date(editedStartTime);
      const end = new Date(editedEndTime);

      if (end <= start) {
        setEditLogError("End time must be after start time.");
        return;
      }

      const diffMs = end - start;
      const diffHours = diffMs / (1000 * 60 * 60);

      await axios.put(
        `${API_BASE_URL}/participations/update-log/${editingLog._id}`,
        {
          startTime: start.toISOString(),
          endTime: end.toISOString(),
          hours: diffHours,
        },
        { headers: getAuthHeader() }
      );

      setEditLogSuccess(true);
      loadData();

      setTimeout(() => {
        setShowEditLogModal(false);
        setEditLogSuccess(false);
      }, 1500);
    } catch (error) {
      setEditLogError(
        error.response?.data?.message || error.response?.data?.error || "Failed to update log. Please try again."
      );
    }
  };

  if (projectsLoading && !project) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!project) {
    return (
      <Container className="py-5 text-center">
        <Alert variant="warning" className="mb-4">
          Project details could not be found.
        </Alert>
        <Button variant="primary" onClick={() => navigate("/admin/projects")}>
          <FaArrowLeft className="me-2" /> Back to Projects list
        </Button>
      </Container>
    );
  }

  // Calculate statistics
  const totalParticipants = participants.length;
  const approvedHours = logs.reduce(
    (sum, log) => sum + (log.status === "approved" ? log.hours : 0),
    0
  );

  const pendingHours = logs.reduce(
    (sum, log) => sum + (log.status === "pending" ? log.hours : 0),
    0
  );

  const pendingLogsCount = logs.filter((log) => log.status === "pending").length;

  const openLightbox = (imgUrl) => {
    setLightboxImage(imgUrl);
    setLightboxOpen(true);
  };

  // Styles definition
  const headerCardStyle = {
    backgroundColor: "var(--color-primary)",
    borderRadius: "var(--radius-lg)",
    border: "none",
    color: "#fff",
    boxShadow: "var(--shadow-md)",
  };

  return (
    <div className="d-flex flex-column flex-lg-row" style={{ maxWidth: "100vw", overflowX: "hidden", backgroundColor: "var(--color-bg)" }}>
      <Sidebar />

      <Container fluid className="py-3 px-4" style={{ minHeight: "100vh", maxHeight: "100vh", overflowY: "scroll" }}>
        <TopNavBar />

        {/* Back Button & Navigation */}
        <div className="d-flex align-items-center justify-content-between mt-3 mb-3">
          <Button
            variant="link"
            onClick={() => navigate("/admin/projects")}
            className="text-decoration-none p-0 d-flex align-items-center"
            style={{ color: "var(--color-primary)", fontWeight: "bold" }}
          >
            <FaArrowLeft className="me-2" /> Back to Projects list
          </Button>
          <Button
            variant="danger"
            className="fw-bold px-3 py-2 border-0"
            style={{ borderRadius: "var(--radius-md)" }}
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete Project
          </Button>
        </div>

        {/* Success / Error Alerts */}
        {actionError && <Alert variant="danger" onClose={() => setActionError(null)} dismissible>{actionError}</Alert>}
        {actionSuccess && <Alert variant="success" onClose={() => setActionSuccess(null)} dismissible>{actionSuccess}</Alert>}
        {pendingAmendment && (
          <Alert variant="warning">
            <div className="d-flex justify-content-between align-items-start gap-3">
              <div>
                <strong>Project amendment awaiting review</strong>
                <div className="small mt-2">
                  {Object.keys(pendingAmendment.changes || {}).join(", ") || "Project details"}
                </div>
              </div>
              <div className="text-nowrap">
                <Button size="sm" variant="success" className="me-2" onClick={() => reviewAmendment("approved")}>Approve</Button>
                <Button size="sm" variant="outline-danger" onClick={() => reviewAmendment("rejected")}>Reject</Button>
              </div>
            </div>
          </Alert>
        )}

        {/* Premium Header Card */}
        <Card className="mb-4 p-4" style={headerCardStyle}>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                <span className={`px-3 py-1.5 fw-bold ${project.approved ? "badge-teal" : project.approved === false ? "badge-red" : "badge-gold"}`} style={{ fontSize: "0.8rem", borderRadius: "20px" }}>
                  {project.approved ? "Approved" : project.approved === false ? "Denied" : "Pending"}
                </span>
                <span className="badge-primary px-3 py-1.5 fw-bold" style={{ fontSize: "0.8rem", borderRadius: "20px" }}>
                  {project.organiserType}
                </span>
              </div>
              <h2 className="display-6 fw-bold mb-2">{project.name}</h2>
              <p className="lead mb-0 text-white-50">Organised by <strong>{project.organiserName}</strong></p>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <p className="mb-1 text-white-50"><FaCalendarAlt className="me-2" /> Started: {new Date(project.startDate).toLocaleDateString()}</p>
              {project.endDate && (
                <p className="mb-0 text-white-50"><FaCalendarAlt className="me-2" /> Ending: {new Date(project.endDate).toLocaleDateString()}</p>
              )}
            </Col>
          </Row>
        </Card>

        {/* Premium Stats Grid */}
        <Row className="mb-4">
          <Col xs={12} md={3} className="mb-3">
            <Card className="p-4 text-center h-100 border-0 shadow-sm" style={{ backgroundColor: "var(--color-surface)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-border)" }}>
              <div className="d-flex align-items-center justify-content-center mb-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", backgroundColor: "rgba(27, 58, 135, 0.08)", color: "var(--color-primary)" }}>
                  <FaUsers size={20} />
                </div>
              </div>
              <div className="text-muted small fw-semibold uppercase mb-1">Total Enrolled</div>
              {participantsLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <h3 className="fw-bold mb-0" style={{ color: "var(--color-primary)", fontSize: "1.8rem" }}>{totalParticipants}</h3>
              )}
            </Card>
          </Col>
          <Col xs={12} md={3} className="mb-3">
            <Card className="p-4 text-center h-100 border-0 shadow-sm" style={{ backgroundColor: "var(--color-surface)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-border)" }}>
              <div className="d-flex align-items-center justify-content-center mb-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", backgroundColor: "rgba(42, 157, 143, 0.08)", color: "var(--color-teal)" }}>
                  <FaClock size={20} />
                </div>
              </div>
              <div className="text-muted small fw-semibold uppercase mb-1">Approved Hours</div>
              {logsLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <h3 className="fw-bold mb-0" style={{ color: "var(--color-teal)", fontSize: "1.8rem" }}>{approvedHours.toFixed(1)} <span style={{ fontSize: "1rem" }}>hrs</span></h3>
              )}
            </Card>
          </Col>
          <Col xs={12} md={3} className="mb-3">
            <Card className="p-4 text-center h-100 border-0 shadow-sm" style={{ backgroundColor: "var(--color-surface)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-border)" }}>
              <div className="d-flex align-items-center justify-content-center mb-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", backgroundColor: "rgba(181, 131, 42, 0.08)", color: "var(--color-gold)" }}>
                  <FaClock size={20} />
                </div>
              </div>
              <div className="text-muted small fw-semibold uppercase mb-1">Pending Hours</div>
              {logsLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <h3 className="fw-bold mb-0" style={{ color: "var(--color-gold)", fontSize: "1.8rem" }}>{pendingHours.toFixed(1)} <span style={{ fontSize: "1rem" }}>hrs</span></h3>
              )}
            </Card>
          </Col>
          <Col xs={12} md={3} className="mb-3">
            <Card className="p-4 text-center h-100 border-0 shadow-sm" style={{ backgroundColor: "var(--color-surface)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-border)" }}>
              <div className="d-flex align-items-center justify-content-center mb-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", backgroundColor: "rgba(193, 57, 43, 0.08)", color: "var(--color-red)" }}>
                  <FaInfoCircle size={20} />
                </div>
              </div>
              <div className="text-muted small fw-semibold uppercase mb-1">Pending Logs</div>
              {logsLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <h3 className="fw-bold mb-0" style={{ color: "var(--color-red)", fontSize: "1.8rem" }}>
                  {pendingLogsCount} <span style={{ fontSize: "0.85rem", fontWeight: "600", color: "var(--color-text-secondary)" }}>submissions</span>
                </h3>
              )}
            </Card>
          </Col>
        </Row>

        {/* Navigation Tabs */}
        <div className="d-flex border-bottom pb-0 mb-4 overflow-auto" style={{ borderColor: "var(--color-border)" }}>
          {[
            { id: "overview", label: "Overview", icon: <FaInfoCircle className="me-2" /> },
            { id: "participants", label: `Participants (${totalParticipants})`, icon: <FaUsers className="me-2" /> },
            { 
              id: "logs", 
              label: "Hour Log Sheets", 
              icon: <FaClock className="me-2" />, 
              badge: pendingLogsCount > 0 ? <span className="badge-red px-2 py-0.5 ms-2" style={{ fontSize: "0.75rem", borderRadius: "10px" }}>{pendingLogsCount}</span> : null 
            },
            { id: "reflections", label: `Student Reflections (${uniqueReflections.length})`, icon: <FaBookOpen className="me-2" /> }
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="d-flex align-items-center bg-transparent border-0 px-3 py-2.5 fw-bold text-nowrap"
                style={{
                  color: isActive ? "var(--color-primary)" : "var(--color-text-secondary)",
                  borderBottom: isActive ? "3px solid var(--color-teal)" : "3px solid transparent",
                  transition: "all var(--transition)",
                  fontSize: "0.95rem"
                }}
              >
                {tab.icon}
                {tab.label}
                {tab.badge}
              </button>
            );
          })}
        </div>

        {/* Tab Contents */}
        {activeTab === "overview" && (
          <Row>
            <Col lg={8} className="mb-4">
              <Card className="border-0 shadow-sm p-4 h-100" style={{ borderRadius: "var(--radius-lg)" }}>
                <h4 className="fw-bold mb-3" style={{ color: "var(--color-primary)" }}>About Project</h4>
                <p style={{ fontSize: "1.05rem", lineHeight: "1.6", color: "var(--color-text-secondary)" }}>{project.description}</p>
                
                <h5 className="fw-bold mt-4 mb-2" style={{ color: "var(--color-primary)" }}>Categories</h5>
                <div className="d-flex flex-wrap gap-2 mb-4">
                  {project.categories?.map((cat, idx) => (
                    <span key={idx} className="badge-primary px-3 py-2 fw-semibold" style={{ fontSize: "0.85rem", borderRadius: "20px" }}>{cat}</span>
                  ))}
                </div>

                <h5 className="fw-bold mb-2" style={{ color: "var(--color-primary)" }}>Sustainable Development Goals (SDGs)</h5>
                <div className="d-flex flex-wrap gap-2">
                  {project.sdgs?.map((sdg, idx) => (
                    <span key={idx} className="badge-teal px-3 py-2 fw-semibold" style={{ fontSize: "0.85rem", borderRadius: "20px" }}>{sdg}</span>
                  ))}
                </div>
              </Card>
            </Col>
            <Col lg={4} className="mb-4">
              <Card className="border-0 shadow-sm p-4 h-100" style={{ borderRadius: "var(--radius-lg)" }}>
                <h4 className="fw-bold mb-3" style={{ color: "var(--color-primary)" }}>Metadata Info</h4>
                <Table hover size="sm" className="mb-0">
                  <tbody>
                    <tr>
                      <th className="py-2 border-0 text-muted" style={{ fontWeight: "normal" }}>Organiser Type</th>
                      <td className="py-2 border-0 fw-bold" style={{ color: "var(--color-text)" }}>{project.organiserType}</td>
                    </tr>
                    <tr>
                      <th className="py-2 text-muted" style={{ fontWeight: "normal" }}>Location Mode</th>
                      <td className="py-2 fw-bold">
                        <span className="badge-primary px-3 py-1 fw-bold" style={{ borderRadius: "12px" }}>{project.locationType}</span>
                      </td>
                    </tr>
                    {project.location?.address && (
                      <tr>
                        <th className="py-2 text-muted" style={{ fontWeight: "normal" }}><FaMapMarkerAlt className="me-1" /> Address</th>
                        <td className="py-2 fw-bold" style={{ fontSize: "0.9rem", color: "var(--color-text)" }}>{project.location.address}</td>
                      </tr>
                    )}
                    {project.weeklyHoursRequired && (
                      <tr>
                        <th className="py-2 text-muted" style={{ fontWeight: "normal" }}>Weekly Hours</th>
                        <td className="py-2 fw-bold" style={{ color: "var(--color-text)" }}>
                          {project.weeklyHoursRequired.minimum || 0} - {project.weeklyHoursRequired.maximum || "N/A"} hrs
                        </td>
                      </tr>
                    )}
                    {project.location?.transportProvided !== undefined && (
                      <tr>
                        <th className="py-2 text-muted" style={{ fontWeight: "normal" }}>Transport</th>
                        <td className="py-2 fw-bold">
                          {project.location.transportProvided ? (
                            <span className="badge-teal px-2 py-1 fw-bold" style={{ borderRadius: "12px" }}>Provided</span>
                          ) : (
                            <span className="badge-primary px-2 py-1 fw-bold" style={{ borderRadius: "12px" }}>Not Provided</span>
                          )}
                        </td>
                      </tr>
                    )}
                    {project.location?.transportDetails && (
                      <tr>
                        <th className="py-2 text-muted" style={{ fontWeight: "normal" }}>Transport Details</th>
                        <td className="py-2" style={{ fontSize: "0.85rem", color: "var(--color-text-secondary)" }}>{project.location.transportDetails}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card>
            </Col>
          </Row>
        )}

        {activeTab === "participants" && (
          <Card className="border-0 shadow-sm p-4" style={{ borderRadius: "var(--radius-lg)" }}>
            <h4 className="fw-bold mb-3" style={{ color: "var(--color-primary)" }}>Enrolled Volunteers</h4>
            {participantsLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : participants.length === 0 ? (
              <Alert variant="info">No participants currently registered for this project.</Alert>
            ) : (
              <div className="table-responsive">
                <Table hover align="middle" className="mb-0">
                  <thead style={{ backgroundColor: "rgba(27, 58, 135, 0.05)", borderBottom: "2px solid var(--color-border)" }}>
                    <tr>
                      <th className="py-3" style={{ borderTopLeftRadius: "var(--radius-md)", color: "var(--color-primary)", fontWeight: "700" }}>Name</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>Email</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>Role</th>
                      <th className="py-3" style={{ borderTopRightRadius: "var(--radius-md)", color: "var(--color-primary)", fontWeight: "700" }}>Date Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((p) => (
                      <tr key={p._id} className="align-middle">
                        <td className="fw-bold" style={{ color: "var(--color-text)" }}>{p.student?.name || "Unknown Volunteer"}</td>
                        <td style={{ color: "var(--color-text-secondary)" }}>{p.student?.email || "N/A"}</td>
                        <td>
                          <span className={`px-2 py-1 fw-bold ${p.role === "leader" ? "badge-gold" : "badge-primary"}`} style={{ fontSize: "0.75rem", borderRadius: "12px" }}>
                            {p.role?.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ color: "var(--color-text-secondary)" }}>{new Date(p.dateJoined).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>
        )}

        {activeTab === "logs" && (
          <Card className="border-0 shadow-sm p-4" style={{ borderRadius: "var(--radius-lg)" }}>
            <h4 className="fw-bold mb-3" style={{ color: "var(--color-primary)" }}>Hours Log Approval Center</h4>
            
            {logsLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : logs.length === 0 ? (
              <Alert variant="info">No hours log entries found for this project.</Alert>
            ) : (
              <div className="table-responsive">
                <Table hover align="middle" className="mb-0">
                  <thead style={{ backgroundColor: "rgba(27, 58, 135, 0.05)", borderBottom: "2px solid var(--color-border)" }}>
                    <tr>
                      <th className="py-3" style={{ borderTopLeftRadius: "var(--radius-md)", color: "var(--color-primary)", fontWeight: "700" }}>Volunteer</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>Start Time</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>End Time</th>
                      <th className="text-center py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>Hours Logged</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>Reflection & Evidence</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>Status</th>
                      <th className="text-end py-3" style={{ borderTopRightRadius: "var(--radius-md)", paddingRight: "20px", color: "var(--color-primary)", fontWeight: "700" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log._id} className="align-middle">
                        <td className="fw-bold" style={{ color: "var(--color-text)" }}>{log.participantName || "Unknown"}</td>
                        <td style={{ color: "var(--color-text-secondary)" }}>{new Date(log.startTime).toLocaleString()}</td>
                        <td style={{ color: "var(--color-text-secondary)" }}>{new Date(log.endTime).toLocaleString()}</td>
                        <td className="text-center fw-bold" style={{ color: "var(--color-primary)" }}>{log.hours.toFixed(2)}</td>
                        <td style={{ minWidth: "190px" }}>
                          {log.reflection ? <div className="small mb-1">{log.reflection}</div> : <span className="text-muted small">No reflection</span>}
                          {log.evidence?.map((url, index) => (
                            <a key={url} href={url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary me-1 mt-1">
                              Evidence {index + 1}
                            </a>
                          ))}
                        </td>
                        <td>
                          <span className={`px-2.5 py-1 fw-bold ${
                            log.status === "approved"
                              ? "badge-teal"
                              : log.status === "rejected"
                              ? "badge-red"
                              : "badge-gold"
                          }`} style={{ fontSize: "0.75rem", borderRadius: "12px", display: "inline-block" }}>
                            {log.status?.toUpperCase()}
                          </span>
                        </td>
                        <td className="text-end" style={{ paddingRight: "20px" }}>
                          <div className="d-flex justify-content-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApproveDenyLog(log._id, "approved")}
                              disabled={log.status === "approved"}
                              className="d-flex align-items-center justify-content-center border-0"
                              style={{ backgroundColor: "var(--color-teal)", color: "#fff", width: "32px", height: "32px", borderRadius: "var(--radius-sm)", opacity: log.status === "approved" ? 0.4 : 1 }}
                              title="Approve"
                            >
                              <FaCheck size={14} />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleApproveDenyLog(log._id, "rejected")}
                              disabled={log.status === "rejected"}
                              className="d-flex align-items-center justify-content-center border-0"
                              style={{ backgroundColor: "var(--color-red)", color: "#fff", width: "32px", height: "32px", borderRadius: "var(--radius-sm)", opacity: log.status === "rejected" ? 0.4 : 1 }}
                              title={log.status === "approved" ? "Revoke Approval" : "Reject"}
                            >
                              <FaTimes size={14} />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleEditLogClick(log)}
                              className="d-flex align-items-center justify-content-center border-0"
                              style={{ backgroundColor: "var(--color-primary)", color: "#fff", width: "32px", height: "32px", borderRadius: "var(--radius-sm)" }}
                              title="Edit Times"
                            >
                              <FaEdit size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>
        )}

        {activeTab === "reflections" && (
          <Card className="border-0 shadow-sm p-4" style={{ borderRadius: "var(--radius-lg)" }}>
            <h4 className="fw-bold mb-4" style={{ color: "var(--color-primary)" }}>Volunteer Reflections</h4>
            {uniqueReflections.length === 0 ? (
              <Alert variant="info">No student reflections have been uploaded for this project yet.</Alert>
            ) : (
              <Row>
                {uniqueReflections.map((ref, idx) => {
                  const studentName = ref.createdBy?.name || ref.studentName || "Volunteer";
                  const firstLetter = studentName.charAt(0).toUpperCase();
                  return (
                    <Col md={6} key={ref._id || idx} className="mb-4">
                      <Card className="border-0 h-100 shadow-sm" style={{ borderRadius: "var(--radius-md)", backgroundColor: "var(--color-surface)" }}>
                        <Card.Body className="d-flex flex-column p-4">
                          {/* Premium Avatar Header */}
                          <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                            <div className="d-flex align-items-center">
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center fw-bold me-3 text-white shadow-sm"
                                style={{
                                  width: "45px",
                                  height: "45px",
                                  backgroundColor: "var(--color-teal)",
                                  fontSize: "1.2rem",
                                  border: "2px solid #fff"
                                }}
                              >
                                {firstLetter}
                              </div>
                              <div className="text-start">
                                <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "1.05rem", color: "var(--color-text)" }}>{studentName}</h6>
                                <small className="text-muted" style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                                  Posted on {new Date(ref.createdAt).toLocaleDateString()}
                                </small>
                              </div>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              title="Delete reflection"
                              aria-label={`Delete reflection by ${studentName}`}
                              disabled={deletingReflectionId === ref._id}
                              onClick={() => handleDeleteReflection(ref._id)}
                            >
                              {deletingReflectionId === ref._id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <FaTrash size={12} />
                              )}
                            </Button>
                          </div>
                          
                          {/* Stylized reflection text block */}
                          <p className="flex-grow-1 mb-3 text-start" style={{ fontSize: "0.95rem", lineHeight: "1.6", fontStyle: "italic", color: "var(--color-text-secondary)" }}>
                            "{ref.text}"
                          </p>

                          {/* Image Gallery */}
                          {ref.pictures && ref.pictures.length > 0 && (
                            <Row className="mt-2 g-2">
                              {ref.pictures.map((picUrl, pIdx) => (
                                <Col xs={4} key={pIdx}>
                                  <div
                                    className="ratio ratio-4x3 rounded shadow-sm"
                                    style={{
                                      backgroundImage: `url(${picUrl})`,
                                      backgroundSize: "cover",
                                      backgroundPosition: "center",
                                      cursor: "pointer",
                                      borderRadius: "8px",
                                      transition: "all 0.2s ease"
                                    }}
                                    onClick={() => openLightbox(picUrl)}
                                    onMouseOver={(e) => {
                                      e.currentTarget.style.transform = "scale(1.04)";
                                      e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                                    }}
                                    onMouseOut={(e) => {
                                      e.currentTarget.style.transform = "scale(1)";
                                      e.currentTarget.style.boxShadow = "0 2px 5px rgba(0,0,0,0.1)";
                                    }}
                                  />
                                </Col>
                              ))}
                            </Row>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card>
        )}
      </Container>

      {/* Lightbox Image Preview Modal */}
      <Modal show={lightboxOpen} onHide={() => setLightboxOpen(false)} centered size="lg">
        <Modal.Body className="p-0 text-center bg-dark rounded">
          <div className="position-relative">
            <Button
              variant="close"
              className="btn-close-white position-absolute top-0 end-0 m-3"
              onClick={() => setLightboxOpen(false)}
            />
            <img src={lightboxImage} alt="Reflection Gallery" className="img-fluid rounded" style={{ maxHeight: "80vh" }} />
          </div>
        </Modal.Body>
      </Modal>

      {/* Delete Project Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => { if (!deleteLoading) setShowDeleteConfirm(false); }} centered>
        <Modal.Header closeButton style={{ borderBottom: "1px solid var(--color-border)" }} className="pb-3">
          <Modal.Title className="fw-bold text-danger">
            Confirm Project Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <p className="text-start mb-3">
            Are you sure you want to permanently delete the project <strong>{project.name}</strong>?
          </p>
          <p className="text-start text-danger mb-4 small fw-semibold">
            WARNING: This action will permanently erase this project and all associated volunteering hours, logs, and student reflections.
          </p>
          <Form onSubmit={handleDeleteProjectSubmit}>
            <Form.Group className="mb-3 text-start">
              <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Type <strong>DELETE</strong> to confirm:</Form.Label>
              <Form.Control
                type="text"
                placeholder="Type DELETE..."
                value={deleteConfirmInput}
                onChange={(e) => setDeleteConfirmInput(e.target.value)}
                required
                style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                disabled={deleteLoading}
                style={{ borderRadius: "var(--radius-sm)" }}
                onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmInput(""); }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={deleteLoading || deleteConfirmInput !== "DELETE"}
                style={{ backgroundColor: "var(--color-red)", color: "#fff", borderRadius: "var(--radius-sm)", border: "none" }}
              >
                {deleteLoading ? "Deleting..." : "Permanently Delete"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Log Times Modal */}
      <Modal show={showEditLogModal} onHide={() => setShowEditLogModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid var(--color-border)" }}>
          <Modal.Title style={{ color: "var(--color-primary)", fontWeight: "700" }}>Edit Log Times</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editLogError && <Alert variant="danger">{editLogError}</Alert>}
          {editLogSuccess && <Alert variant="success">Log updated successfully!</Alert>}
          <Form onSubmit={handleEditLogSubmit}>
            <Form.Group className="mb-3" controlId="adminEditStartTime">
              <Form.Label style={{ fontWeight: "600" }}>Start Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={editedStartTime}
                onChange={(e) => setEditedStartTime(e.target.value)}
                style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="adminEditEndTime">
              <Form.Label style={{ fontWeight: "600" }}>End Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={editedEndTime}
                onChange={(e) => setEditedEndTime(e.target.value)}
                style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid var(--color-border)" }}>
          <Button variant="secondary" onClick={() => setShowEditLogModal(false)} style={{ borderRadius: "var(--radius-sm)" }}>
            Cancel
          </Button>
          <Button
            onClick={handleEditLogSubmit}
            style={{ backgroundColor: "var(--color-primary)", color: "#fff", border: "none", borderRadius: "var(--radius-sm)" }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminProjectDetailView;
